/**
 * Sub-agent transcript API (drill-in modal).
 *
 * One sub-agent's own stream — its reasoning, tool calls and text — which the
 * main transcript deliberately filters out (backed by
 * ``GET /conversations/{id}/subagents/{tool_use_id}/messages``, schema v35).
 *
 * The key is the ``Task`` tool call that spawned the sub-agent, which is the
 * same id shown on its tool card and in the "Working" indicator, so the client
 * always has it. While the sub-agent is still running the modal re-polls, so a
 * partial transcript is the normal steady state, not an error.
 */

import { apiFetch } from "./client";
import type { Message } from "./types";

export interface SubagentTranscript {
  messages: Message[];
  /** True when the sub-agent emitted more rows than the endpoint's cap. */
  truncated: boolean;
  /** The sub-agent's answer, once it has returned one.
   *
   *  Not part of ``messages``: a sub-agent hands its report back as the *Task
   *  tool's* result, which is a main-agent row. Sub-agent turns are often pure
   *  tool calls with no text of their own, so this is usually the only prose
   *  the drill-in has to show. */
  final_report: { content: string; is_error: boolean } | null;
}

export async function fetchSubagentTranscript(
  conversationId: string,
  toolUseId: string,
): Promise<SubagentTranscript> {
  return apiFetch<SubagentTranscript>(
    `/conversations/${conversationId}/subagents/${encodeURIComponent(toolUseId)}/messages`,
  );
}
